import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import PerMachineItem from './PerMachineItem';
import { Button, Form } from 'react-bootstrap';
import dayjs from 'dayjs';
import {
  OrderOption,
  perMachineSummarySalesData,
  perMachineTotalSalesData,
} from 'types/perMachineTypes';
import ModifyMachineModal from './ModifyMachineModal';
import Cookies from 'js-cookie';
import { AUTH } from '@dataTypes/authTypes';
import { useRecoilValue } from 'recoil';
import storeInfo from '../../recoil/storeInfo/atom';
import { isMileageDisplayEnabled } from '@utils/mileageDisplay';
import { useGetMachineTotalSalesList } from '@hooks/perMachine/useGetMachineTotalSalesList';
import Loading from '@components/common/loading/LoadingSpinner';
import ServiceCoinModal from './ServiceCoinModal';
import RemoteControlModal from './RemoteControlModal';
import PerMachineSortDropdown from './PerMachineSortDropdown';
import DatePicker from '@components/totalSales/DatePicker';
import PaymentHistoryModal from './PaymentHistoryModal';
import { getStockAll, getWebSocketTerminals } from '@apis/perMachine';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMACHINE } from '@components/perMachine/queryKey/queryKey';
import type { RemoteControlMachine } from './RemoteControlModal';
import type { TerminalC0Info } from './ServiceCoinModal';
import { isSharedCatId, SHARED_CATID_S0_BLOCKED_MESSAGE } from './sharedCatIdMessages';

const normalizeCatId = (catId: string) => catId.trim();

const normalizeCatIdKey = (catId: string) => {
  const key = catId.trim();
  return /^\d+$/.test(key) ? String(Number(key)) : key;
};

type ConnectionFilter = 'all' | 'connected' | 'disconnected';

const PerMachineContainer = () => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showModify, setShowModify] = useState(false);
  const [showServiceCoinModal, setShowServiceCoinModal] = useState(false);
  const [showRemoteControlModal, setShowRemoteControlModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [serviceModalMachines, setServiceModalMachines] = useState<RemoteControlMachine[]>([]);
  const [remoteModalMachines, setRemoteModalMachines] = useState<RemoteControlMachine[]>([]);
  const [selectedCatIds, setSelectedCatIds] = useState(() => new Set<string>());
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
  const [selectedMachineData, setSelectedMachineData] = useState<
    perMachineTotalSalesData | perMachineSummarySalesData | undefined
  >(undefined);
  const [orderDropdownOpen, setOrderDropdownOpen] = useState(false);
  const [orderOption, setOrderOption] = useState<OrderOption | undefined>(undefined);
  const [connectionFilter, setConnectionFilter] = useState<ConnectionFilter>('all');
  const [newDate, setNewDate] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection',
    },
  ]);
  const authority = Cookies.get('smartlab_authority');
  const convertDate = (selectedDateVal: Date) => {
    return dayjs(selectedDateVal).format('YYYYMMDD');
  };
  const storeInfoState = useRecoilValue(storeInfo);
  const showMileage = isMileageDisplayEnabled(
    storeInfoState.currentStoreId,
    storeInfoState.storeLists,
  );
  const { getPerMachineSaleData, getPerMachineSummarySaleData, isLoading } =
    useGetMachineTotalSalesList(
      storeInfoState.currentStoreId,
      convertDate(newDate[0].startDate),
      convertDate(newDate[0].endDate),
      orderOption,
    );

  const isSameDayView = convertDate(newDate[0].startDate) === convertDate(newDate[0].endDate);

  const visibleList = useMemo((): (perMachineTotalSalesData | perMachineSummarySalesData)[] => {
    if (isLoading) return [];
    if (isSameDayView) return getPerMachineSaleData ?? [];
    return getPerMachineSummarySaleData ?? [];
  }, [isLoading, isSameDayView, getPerMachineSaleData, getPerMachineSummarySaleData]);

  const dateRangeKey = `${convertDate(newDate[0].startDate)}_${convertDate(newDate[0].endDate)}`;

  useEffect(() => {
    setSelectedCatIds(new Set());
    setConnectionFilter('all');
  }, [storeInfoState.currentStoreId, dateRangeKey]);

  const queryClient = useQueryClient();
  const { data: stockList, isFetching: isStockFetching } = useQuery({
    queryKey: [PERMACHINE.PERMACHINESTOCK, storeInfoState.currentStoreId],
    queryFn: () => getStockAll(storeInfoState.currentStoreId),
    enabled: !!storeInfoState.currentStoreId,
    staleTime: 60000,
  });

  const { data: websocketTerminals, isFetching: isWebSocketTerminalsFetching } = useQuery({
    queryKey: [PERMACHINE.WEBSOCKET_TERMINALS, storeInfoState.currentStoreId],
    queryFn: () => getWebSocketTerminals(storeInfoState.currentStoreId),
    enabled: !!storeInfoState.currentStoreId,
    staleTime: 30000,
  });

  const isRefreshing = isStockFetching || isWebSocketTerminalsFetching;

  type TerminalStatusMap = {
    usageStatus?: string;
    isConnected?: boolean;
    connectedSessionCount?: number | null;
    stock?: number | null;
    stockManagementOn?: boolean | null;
    version?: string | null;
    fwAutoUpdate?: string | null;
    internetNetwork?: string | null;
    displayBrightness?: number | null;
    amount?: string | null;
    pulse?: string | null;
  };
  const terminalStatusByCatId = useMemo(() => {
    if (!websocketTerminals?.length) return {} as Record<string, TerminalStatusMap>;
    return websocketTerminals.reduce<Record<string, TerminalStatusMap>>((acc, t) => {
      if (t.catId == null) return acc;
      const key = t.catId.trim();
      const normalized = /^\d+$/.test(key) ? String(Number(key)) : key;
      acc[key] = {
        usageStatus: t.usageStatus ?? undefined,
        isConnected: t.isConnected,
        connectedSessionCount: t.connectedSessionCount ?? null,
        stock: t.stock ?? null,
        stockManagementOn: t.stockManagementOn ?? null,
        version: t.version ?? null,
        fwAutoUpdate: t.fwAutoUpdate ?? null,
        internetNetwork: t.internetNetwork ?? null,
        displayBrightness: t.displayBrightness ?? null,
        amount: t.amount ?? null,
        pulse: t.pulse ?? null,
      };
      if (normalized !== key) acc[normalized] = acc[key];
      return acc;
    }, {});
  }, [websocketTerminals]);

  const stockByCatId = useMemo(() => {
    if (!stockList?.length) return {} as Record<string, number | null>;
    return stockList.reduce<Record<string, number | null>>((acc, item) => {
      if (item.catId != null) {
        const key = item.catId.trim();
        acc[key] = item.stock ?? null;
        const normalized = /^\d+$/.test(key) ? String(Number(key)) : key;
        if (normalized !== key) acc[normalized] = item.stock ?? null;
      }
      return acc;
    }, {});
  }, [stockList]);

  const getStockForCatId = (catId: string) => {
    if (!catId) return null;
    const trimmed = catId.trim();
    return (
      stockByCatId[trimmed] ??
      stockByCatId[/^\d+$/.test(trimmed) ? String(Number(trimmed)) : trimmed] ??
      null
    );
  };

  const getTerminalStatus = (catId: string) => {
    if (!catId) return undefined;
    const trimmed = catId.trim();
    const normalized = /^\d+$/.test(trimmed) ? String(Number(trimmed)) : trimmed;
    return terminalStatusByCatId[trimmed] ?? terminalStatusByCatId[normalized];
  };

  const isTerminalConnected = useCallback(
    (catId: string) => getTerminalStatus(catId)?.isConnected === true,
    [terminalStatusByCatId],
  );

  const filteredList = useMemo((): (perMachineTotalSalesData | perMachineSummarySalesData)[] => {
    if (connectionFilter === 'all') return visibleList;
    return visibleList.filter((m) => {
      const connected = isTerminalConnected(m.catId);
      return connectionFilter === 'connected' ? connected : !connected;
    });
  }, [visibleList, connectionFilter, isTerminalConnected]);

  const visibleCatIds = useMemo(
    () => filteredList.map((m) => normalizeCatId(m.catId)),
    [filteredList],
  );

  const toggleConnectionFilter = (next: ConnectionFilter) => {
    setConnectionFilter((prev) => {
      const nextFilter = prev === next ? 'all' : next;
      if (nextFilter !== prev) {
        setSelectedCatIds(new Set());
      }
      return nextFilter;
    });
  };

  const allVisibleSelected =
    visibleCatIds.length > 0 && visibleCatIds.every((id) => selectedCatIds.has(id));
  const someVisibleSelected = visibleCatIds.some((id) => selectedCatIds.has(id));

  useEffect(() => {
    const el = selectAllCheckboxRef.current;
    if (el) {
      el.indeterminate = someVisibleSelected && !allVisibleSelected;
    }
  }, [someVisibleSelected, allVisibleSelected]);

  const selectedMachines = useMemo(
    () => filteredList.filter((m) => selectedCatIds.has(normalizeCatId(m.catId))),
    [filteredList, selectedCatIds],
  );

  const toggleCatSelection = (catId: string) => {
    const id = normalizeCatId(catId);
    setSelectedCatIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleSelectAllVisible = () => {
    setSelectedCatIds((prev) => {
      const next = new Set(prev);
      const allOn = visibleCatIds.length > 0 && visibleCatIds.every((id) => next.has(id));
      if (allOn) {
        visibleCatIds.forEach((id) => next.delete(id));
      } else {
        visibleCatIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedCatIds(new Set());

  const connectionStats = useMemo(() => {
    const total = visibleList.length;
    let connected = 0;
    for (const m of visibleList) {
      if (getTerminalStatus(m.catId)?.isConnected) {
        connected += 1;
      }
    }
    return { total, connected, disconnected: total - connected };
  }, [visibleList, terminalStatusByCatId]);

  const getTerminalC0 = useCallback(
    (catId: string): TerminalC0Info | undefined => {
      if (!catId) return undefined;
      const trimmed = catId.trim();
      const normalized = /^\d+$/.test(trimmed) ? String(Number(trimmed)) : trimmed;
      const t = terminalStatusByCatId[trimmed] ?? terminalStatusByCatId[normalized];
      if (!t) return undefined;
      return { amount: t.amount ?? null, pulse: t.pulse ?? null };
    },
    [terminalStatusByCatId],
  );

  const handleServiceCoinModalShow = (
    machineData: perMachineTotalSalesData | perMachineSummarySalesData,
  ) => {
    if (isSharedCatId(getTerminalStatus(machineData.catId)?.connectedSessionCount)) {
      window.alert(SHARED_CATID_S0_BLOCKED_MESSAGE);
      return;
    }
    setServiceModalMachines([machineData]);
    setShowServiceCoinModal(true);
  };
  const handleServiceCoinModalClose = () => {
    setServiceModalMachines([]);
    setShowServiceCoinModal(false);
  };

  const handleRemoteControlModalShow = (
    machineData: perMachineTotalSalesData | perMachineSummarySalesData,
  ) => {
    setRemoteModalMachines([machineData]);
    setShowRemoteControlModal(true);
  };

  const handleRemoteControlModalClose = () => {
    setRemoteModalMachines([]);
    setShowRemoteControlModal(false);
  };

  const openBulkServiceCoinModal = () => {
    if (selectedMachines.length === 0) return;
    const shared = selectedMachines.filter((m) =>
      isSharedCatId(getTerminalStatus(m.catId)?.connectedSessionCount),
    );
    if (shared.length > 0) {
      window.alert(
        `${SHARED_CATID_S0_BLOCKED_MESSAGE}\n\n해당 단말: ${shared
          .map((m) => m.deviceAlias || m.catId)
          .slice(0, 8)
          .join(', ')}${shared.length > 8 ? ` 외 ${shared.length - 8}대` : ''}`,
      );
      return;
    }
    setServiceModalMachines([...selectedMachines]);
    setShowServiceCoinModal(true);
  };

  const openBulkRemoteControlModal = () => {
    if (selectedMachines.length === 0) return;
    setRemoteModalMachines([...selectedMachines]);
    setShowRemoteControlModal(true);
  };

  const handlePaymentHistoryModalShow = (
    machineData: perMachineTotalSalesData | perMachineSummarySalesData,
  ) => {
    setSelectedMachineData(machineData);
    setShowPaymentHistoryModal(true);
  };

  const handlePaymentHistoryModalClose = () => {
    setSelectedMachineData(undefined);
    setShowPaymentHistoryModal(false);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: [PERMACHINE.PERMACHINESTOCK] });
    queryClient.invalidateQueries({ queryKey: [PERMACHINE.WEBSOCKET_TERMINALS] });
    queryClient.invalidateQueries({ queryKey: [PERMACHINE.PERMACHINESALESLISTS] });
    queryClient.invalidateQueries({ queryKey: [PERMACHINE.PERMACHINESUMMARYSALESLISTS] });
  };

  return (
    <div className="per_machine_wrapper" data-tour="per-machine">
      <DatePicker
        showDatePicker={showDatePicker}
        setShowDatePicker={setShowDatePicker}
        newDate={newDate}
        setNewDate={setNewDate}
      />

      <div className="btn_wrapper">
        <PerMachineSortDropdown
          orderValue={orderOption}
          orderChange={setOrderOption}
          orderDropdownOpen={orderDropdownOpen}
          setOrderDropdownOpen={setOrderDropdownOpen}
        />
        <Button
          className="refresh_btn nickname_btn"
          onClick={handleRefresh}
          variant="outline-secondary"
          disabled={isRefreshing}
        >
          {isRefreshing ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              />
              새로고침 중...
            </>
          ) : (
            '새로고침'
          )}
        </Button>
        {authority !== AUTH.USER && (
          <Button className="nickname_btn" onClick={() => setShowModify(true)}>
            단말기별 별칭설정
          </Button>
        )}
      </div>

      {visibleList.length > 0 && (
        <div className="per_machine_connection_stats" aria-label="단말기 WebSocket 접속 현황">
          <button
            type="button"
            className={`connection_stat connection_stat--filter${connectionFilter === 'all' ? ' is-active' : ''}`}
            onClick={() => toggleConnectionFilter('all')}
            aria-pressed={connectionFilter === 'all'}
          >
            등록 단말 <strong>{connectionStats.total}</strong>대
          </button>
          <button
            type="button"
            className={`connection_stat connection_stat--connected connection_stat--filter${connectionFilter === 'connected' ? ' is-active' : ''}`}
            onClick={() => toggleConnectionFilter('connected')}
            aria-pressed={connectionFilter === 'connected'}
          >
            접속 <strong>{connectionStats.connected}</strong>대
          </button>
          <button
            type="button"
            className={`connection_stat connection_stat--disconnected connection_stat--filter${connectionFilter === 'disconnected' ? ' is-active' : ''}`}
            onClick={() => toggleConnectionFilter('disconnected')}
            aria-pressed={connectionFilter === 'disconnected'}
          >
            미접속 <strong>{connectionStats.disconnected}</strong>대
          </button>
          {connectionFilter !== 'all' && (
            <span className="connection_filter_hint">
              {connectionFilter === 'connected' ? '접속' : '미접속'} 단말만 표시 중 (
              {filteredList.length}대)
            </span>
          )}
        </div>
      )}

      {visibleList.length > 0 && (
        <div className="per_machine_selection_bar">
          <div className="selection_bar_left">
            <Form.Check
              ref={selectAllCheckboxRef}
              type="checkbox"
              id="per-machine-select-all-visible"
              label="전체 선택"
              checked={allVisibleSelected}
              onChange={handleToggleSelectAllVisible}
              className="selection_select_all_check"
            />
            <Button
              type="button"
              variant="link"
              size="sm"
              className="selection_clear_btn"
              onClick={clearSelection}
              disabled={selectedCatIds.size === 0}
            >
              선택 해제
            </Button>
            <span className="selection_count">{selectedMachines.length}대 선택</span>
          </div>
          <div className="selection_bar_right">
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              className="selection_bulk_btn"
              onClick={openBulkServiceCoinModal}
              disabled={selectedMachines.length === 0}
            >
              선택 단말 서비스 지급 ({selectedMachines.length})
            </Button>
            <Button
              type="button"
              variant="outline-primary"
              size="sm"
              className="selection_bulk_btn"
              onClick={openBulkRemoteControlModal}
              disabled={selectedMachines.length === 0}
            >
              선택 단말 원격 관리 ({selectedMachines.length})
            </Button>
          </div>
        </div>
      )}

      <div className="per_item_wrapper">
        {!(getPerMachineSummarySaleData && getPerMachineSummarySaleData.length > 0) &&
          !(getPerMachineSaleData && getPerMachineSaleData.length > 0) &&
          isLoading && (
            <div className="loading_wrapper">
              <Loading />
            </div>
          )}

        {!isLoading && visibleList.length === 0 && <div className="no_data">데이터가 없습니다</div>}

        {!isLoading && visibleList.length > 0 && filteredList.length === 0 && (
          <div className="no_data">
            {connectionFilter === 'connected' ? '접속된 단말이 없습니다' : '미접속 단말이 없습니다'}
          </div>
        )}

        {!isLoading &&
          filteredList.map((list) => (
            <PerMachineItem
              key={normalizeCatId(list.catId)}
              item={list}
              showMileage={showMileage}
              stock={getStockForCatId(list.catId)}
              terminalStatus={getTerminalStatus(list.catId)}
              handleServiceCoinModalShow={handleServiceCoinModalShow}
              handlePaymentHistoryModalShow={handlePaymentHistoryModalShow}
              handleRemoteControlModalShow={handleRemoteControlModalShow}
              isSelected={selectedCatIds.has(normalizeCatId(list.catId))}
              onToggleSelect={toggleCatSelection}
            />
          ))}
      </div>

      <ModifyMachineModal handleClose={() => setShowModify(false)} show={showModify} />

      <ServiceCoinModal
        handleClose={handleServiceCoinModalClose}
        show={showServiceCoinModal && serviceModalMachines.length > 0}
        machines={serviceModalMachines}
        sameDate={
          convertDate(newDate[0].startDate) === convertDate(newDate[0].endDate) ? true : false
        }
        businessNum={
          storeInfoState.storeLists?.find((s) => s.storeId === storeInfoState.currentStoreId)
            ?.businessNum
        }
        getTerminalC0={getTerminalC0}
      />

      <RemoteControlModal
        show={showRemoteControlModal && remoteModalMachines.length > 0}
        onClose={handleRemoteControlModalClose}
        machines={remoteModalMachines}
        businessNum={
          storeInfoState.storeLists?.find((s) => s.storeId === storeInfoState.currentStoreId)
            ?.businessNum
        }
        terminalInfo={
          remoteModalMachines.length === 1
            ? getTerminalStatus(remoteModalMachines[0].catId)
            : undefined
        }
        hasSharedCatId={remoteModalMachines.some((m) =>
          isSharedCatId(getTerminalStatus(m.catId)?.connectedSessionCount),
        )}
        onSuccess={() => {
          // 재고량: GET /api/stock, 재고관리 On/Off·연결상태: GET /api/websocket/terminals 둘 다 갱신
          queryClient.invalidateQueries({ queryKey: [PERMACHINE.PERMACHINESTOCK] });
          queryClient.invalidateQueries({ queryKey: [PERMACHINE.WEBSOCKET_TERMINALS] });
        }}
      />

      <PaymentHistoryModal
        showPaymentHistoryModal={showPaymentHistoryModal}
        showPaymentHistoryModalHandleClose={handlePaymentHistoryModalClose}
        machineData={selectedMachineData}
        startDate={newDate[0].startDate}
        endDate={newDate[0].endDate}
        showMileage={showMileage}
      />
    </div>
  );
};

export default PerMachineContainer;
